/* I MODULI DELLA SKIN: come il sito legge quello che il backoffice ha composto.
 *
 * Il sito e' un albero di moduli (home, pieDiPagina, sport, ...) e ogni
 * modulo ha contenuto, forma e colori. Cosa un modulo SA fare sta nel
 * catalogo (catalogo.json); cosa la skin HA scelto arriva da by-domain in
 * skin.moduli, con la stessa forma. Qui si mettono insieme i due:
 *
 *   - un'impostazione che la skin non ha scritta vale «di serie», cioe'
 *     quello che il sito faceva prima di avere i moduli: cosi' una skin
 *     senza moduli si vede identica a ieri, e niente sparisce in silenzio;
 *   - un blocco o un modulo che il catalogo non conosce viene ignorato:
 *     il backoffice lo puo' aver scritto per un sito piu' nuovo di questo.
 *
 * DAL BACKOFFICE PARTE TUTTO: il «di serie» non e' un valore inventato dal
 * sito, e' scritto nel catalogo che anche il backoffice legge e mostra.
 */
import catalogo from './catalogo.json';
import {skinCorrente, ascoltaSkin} from '../skin';
import {indirizzoImmagine} from '../utils/immagini';
import {eTelefono} from '../components/SportMenu/sport-central/schermo';
import {lingua as linguaCorrente} from '../preferenze';

export const CATALOGO = catalogo;

function oggetto(v) {
  return v && typeof v === 'object' && !Array.isArray(v) ? v : null;
}

/**
 * L'albero dei moduli della skin corrente, o un oggetto vuoto, GIA' PER LO
 * SCHERMO su cui si guarda: computer o telefono (sotto i 1024 px, la soglia
 * di schermo.js). Vedi perDispositivo. Si rifa' solo quando cambiano la
 * skin o lo schermo: chi legge un modulo lo chiede a ogni ridisegno.
 */
let ricordo = {da: null, dove: '', lingua: '', albero: {}};
export function moduliDellaSkin() {
  const skin = skinCorrente();
  const moduli = (skin && oggetto(skin.moduli)) || null;
  if (!moduli) return {};
  const dove = eTelefono() ? 'telefono' : 'computer';
  const lingua = linguaCorrente();
  if (ricordo.da !== moduli || ricordo.dove !== dove || ricordo.lingua !== lingua) {
    ricordo = {da: moduli, dove, lingua, albero: nellaLingua(perDispositivo(moduli, dove), lingua)};
  }
  return ricordo.albero;
}

/**
 * I TESTI DELLA SKIN NELLA LINGUA DI CHI GUARDA. Il backoffice scrive i
 * testi dei moduli in italiano (Chi siamo, Aiuto, copertine, piede...) e,
 * accanto, le traduzioni: `moduli.traduzioni = {en: {"<frase italiana>":
 * "<traduzione>"}, ...}`. Qui ogni testo dell'albero che ha la traduzione
 * nella lingua corrente la prende; uno che non ce l'ha resta in italiano.
 * La chiave e' la frase stessa, come nel dizionario del sito: spostare un
 * blocco o una voce non perde la traduzione, e una frase uguale in due
 * posti si traduce una volta.
 */
export function nellaLingua(moduli, lingua) {
  const tutte = oggetto(moduli.traduzioni);
  const mappa = tutte && oggetto(tutte[lingua]);
  if (!mappa || !Object.keys(mappa).length) return moduli;
  const tradotto = (v) => {
    if (typeof v === 'string') {
      const t = Object.prototype.hasOwnProperty.call(mappa, v) ? mappa[v] : null;
      return typeof t === 'string' && t.trim() ? t : v;
    }
    if (Array.isArray(v)) return v.map(tradotto);
    if (oggetto(v)) {
      const fuori = {};
      Object.keys(v).forEach((k) => { fuori[k] = tradotto(v[k]); });
      return fuori;
    }
    return v;
  };
  const fuori = {};
  Object.keys(moduli).forEach((k) => { fuori[k] = k === 'traduzioni' ? moduli[k] : tradotto(moduli[k]); });
  return fuori;
}

/**
 * COMPUTER E TELEFONO SEPARATI. Il backoffice puo' spegnere una parte solo
 * su uno dei due schermi, e lo scrive cosi':
 *
 *   - un blocco della Home ha accanto ad `acceso` anche `computer: false` o
 *     `telefono: false`: su quello schermo il blocco e' spento;
 *   - ogni modulo puo' avere `nascostoSu: {computer: [...], telefono: [...]}`,
 *     i percorsi (dentro il modulo) degli interruttori spenti su quello
 *     schermo: «contenuto.cerca», «contenuto.cripto.acceso» (piede),
 *     «chiSiamo.contenuto.acceso» (una pagina di Aiuto).
 *
 * L'interruttore principale resta acceso se la parte si vede almeno su uno
 * schermo: una skin che non scrive niente si vede uguale dappertutto, come
 * prima. Qui si spegne quello che va spento sullo schermo `dove` e si
 * restituisce l'albero cosi', senza toccare quello della skin: tutte le
 * letture sotto (testata, piede, blocchiHome, …) lo vedono gia' giusto.
 */
export function perDispositivo(moduli, dove) {
  let fuori = moduli;
  Object.keys(moduli).forEach((k) => {
    const m = oggetto(moduli[k]);
    if (!m) return;
    let nuovo = m;
    const nascosti = oggetto(m.nascostoSu) && m.nascostoSu[dove];
    if (Array.isArray(nascosti)) {
      nascosti.forEach((percorso) => {
        if (typeof percorso === 'string' && percorso) nuovo = spegni(nuovo, percorso.split('.'));
      });
    }
    if (k === 'home' && Array.isArray(m.blocchi) && m.blocchi.some((b) => oggetto(b) && b[dove] === false)) {
      nuovo = Object.assign({}, nuovo, {
        blocchi: m.blocchi.map((b) => (oggetto(b) && b[dove] === false ? Object.assign({}, b, {acceso: false}) : b)),
      });
    }
    if (nuovo !== m) {
      if (fuori === moduli) fuori = Object.assign({}, moduli);
      fuori[k] = nuovo;
    }
  });
  return fuori;
}

/* Una copia di `o` con false in fondo al percorso; i pezzi che mancano si creano. */
function spegni(o, parti) {
  const [primo, ...resto] = parti;
  if (!primo || primo === '__proto__' || primo === 'constructor' || primo === 'prototype') return o;
  const base = oggetto(o) || {};
  return Object.assign({}, base, {[primo]: resto.length ? spegni(base[primo], resto) : false});
}

/**
 * Il valore EFFETTIVO di una chiave dell'albero («testata.contenuto.cerca»),
 * col «di serie» del catalogo quando la skin non dice niente: serve alle
 * regole «spentoSe» fra moduli. Oggi sa leggere gli interruttori del
 * contenuto dei moduli di primo livello.
 */
export function leggiChiave(chiave) {
  const [modulo, pannello, campo] = String(chiave || '').split('.');
  if (!modulo || !pannello || !campo) return undefined;
  const def = catalogo.moduli[modulo];
  const defCampo = def && def[pannello] && def[pannello][campo];
  const scelto = oggetto(moduliDellaSkin()[modulo]) || {};
  const v = (oggetto(scelto[pannello]) || {})[campo];
  if (v === undefined || v === null) return defCampo ? defCampo.diSerie !== false : undefined;
  return v !== false;
}

/**
 * LA TESTATA (modulo Testata): lo stile del guscio, orologio e cerca partita,
 * nome e simbolo del logo Nova, dove sta il menu dal computer (in testata o a
 * colonna) e se a scomparsa, la larghezza delle pagine, il saldo riletto
 * mentre si gioca, e i colori. La
 * rotellina e' del modulo Schedina (schedina().rotellina), la lingua del
 * traduttore: qui non si ripetono.
 */
export function testata() {
  const def = catalogo.moduli.testata;
  const scelto = oggetto(moduliDellaSkin().testata) || {};
  const contenuto = oggetto(scelto.contenuto) || {};
  const colori = oggetto(scelto.colori) || {};
  const interruttore = (chiave) => {
    const campo = def.contenuto[chiave];
    const v = contenuto[chiave];
    return v === undefined || v === null ? campo.diSerie !== false : v !== false;
  };
  /* Lo STILE del guscio: «standard» (la striscia, la riga dei giochi, la barra
     e il piede di sempre) o «nova» (components/Nova). Un valore che il
     catalogo non conosce vale quello di serie. */
  const forma = oggetto(scelto.forma) || {};
  const campoStile = (def.forma || {}).stile;
  const ammessi = campoStile ? campoStile.scelte.map((s) => s.valore) : [];
  /* Il simbolo del logo Nova: moneta, blocco o nessuno; sconosciuto = di serie. */
  const campoIcona = (def.forma || {}).icona;
  const icone = campoIcona ? campoIcona.scelte.map((s) => s.valore) : [];
  return {
    orologio: interruttore('orologio'),
    cerca: interruttore('cerca'),
    stile: ammessi.includes(forma.stile) ? forma.stile : (campoStile ? campoStile.diSerie : 'standard'),
    /* Il nome nel logo Nova: vuoto = il nome della skin (lo decide chi disegna). */
    marchio: typeof contenuto.marchio === 'string' ? contenuto.marchio.trim() : '',
    icona: icone.includes(forma.icona) ? forma.icona : (campoIcona ? campoIcona.diSerie : 'nessuna'),
    colori: colori.eredita === false ? colori : null,
    /* DOVE STA IL MENU dal computer, con Nova: «testata» (le sezioni in alto
       accanto al logo, niente colonna, la pagina larga tutto lo schermo) o
       «colonna» (la colonna fissa a sinistra). Sul telefono non cambia niente. */
    menu: sceltaDi((def.forma || {}).menu, forma.menu, 'testata'),
    /* Il menu di sinistra a scomparsa: conta solo col menu a colonna. */
    menuAScomparsa: interruttore('menuAScomparsa'),
    /* Le categorie in tendina sotto Casino' e Live casino, col menu in testata: di serie no, solo il collegamento. */
    schedeInTendina: interruttore('schedeInTendina'),
    /* Quanto si allargano le pagine con Nova: «piena» (tutto lo schermo) o
       «limitata» (fino a larghezzaPagina). Il guscio di sempre non la legge. */
    larghezza: sceltaDi((def.forma || {}).larghezza, forma.larghezza, 'piena'),
    /* Il saldo riletto mentre un gioco e' aperto: ogni quanti secondi, 0 = spento. */
    saldoOgni: interruttore('saldoInGioco') ? secondiDelSaldo(def.contenuto.saldoOgni, contenuto.saldoOgni) : 0,
    /* Quanto si allargano le pagine dal computer, in pixel (--lvc-pagina-max). */
    larghezzaPagina: numeroNeiLimiti(def.contenuto.larghezzaPagina, contenuto.larghezzaPagina),
    /* I tasti del menu in testata con Nova: «rilievo» (lastre in 3D) o «piatti». */
    tasti: sceltaDi((def.forma || {}).tasti, forma.tasti, 'rilievo'),
    /* La lingua piccola accanto a Esci, col menu in testata. */
    linguaInTestata: interruttore('linguaInTestata'),
  };
}

/** Una scelta del catalogo: il valore scritto se e' fra le scelte, se no quello di serie (o `altrimenti` senza catalogo). */
function sceltaDi(campo, scelto, altrimenti) {
  const ammessi = campo && Array.isArray(campo.scelte) ? campo.scelte.map((s) => s.valore) : [];
  if (ammessi.includes(scelto)) return scelto;
  return campo && campo.diSerie !== undefined ? campo.diSerie : altrimenti;
}

/** Un numero del catalogo dentro i suoi limiti; niente di scritto (o non un numero) vale il «di serie». */
function numeroNeiLimiti(campo, scelto) {
  const limiti = campo || {};
  const n = Number(scelto);
  if (scelto === undefined || scelto === null || scelto === '' || !Number.isFinite(n) || n <= 0) return limiti.diSerie;
  return Math.round(Math.min(Number(limiti.massimo) || n, Math.max(Number(limiti.minimo) || 0, n)));
}

/** I secondi del saldo in gioco, dentro i limiti del catalogo; niente di scritto vale il «di serie». */
function secondiDelSaldo(campo, scelto) {
  const limiti = campo || {};
  const minimo = Number(limiti.minimo) || 3;
  const massimo = Number(limiti.massimo) || 60;
  const n = Number(scelto);
  if (scelto === undefined || scelto === null || scelto === '' || !Number.isFinite(n) || n <= 0) return Number(limiti.diSerie) || 5;
  return Math.min(massimo, Math.max(minimo, n));
}

/**
 * Le sezioni di gioco: accesa se la skin non dice niente (com'era prima
 * degli interruttori), spenta solo se il backoffice l'ha spenta.
 * Le chiavi sono quelle del catalogo: sport, liveSport, sportBetby, casino,
 * liveCasino, poker, virtual, crashGame.
 *
 * Una sezione nuova col «diSerie: false» nel catalogo (Sport Betby) parte
 * spenta: le skin che non la nominano non se la trovano nel menu da sole.
 */
export function sezioneAccesa(chiave) {
  const skin = skinCorrente();
  const sezioni = skin && oggetto(skin.sections);
  if (!sezioni || !(chiave in sezioni)) {
    const def = catalogo.sezioni.find((s) => s.chiave === chiave);
    return !def || def.diSerie !== false;
  }
  return sezioni[chiave] !== false;
}

/**
 * LE SCOMMESSE CI SONO SE LA SKIN HA LO SPORT (3 novembre 2026): sport o
 * sport live accesi. Le voci del gettone (SITE_BETS_VIEW) sono del livello e
 * valgono per tutte le skin; senza questo controllo un WEB_USER di una skin
 * senza sport vedeva «Lista scommesse» nel menu.
 */
export function skinConScommesse() {
  return sezioneAccesa('sport') || sezioneAccesa('liveSport');
}

/**
 * IL REPORT CASINO' C'E' SE LA SKIN HA UN GIOCO DA CASINO' (2 novembre
 * 2026): casino', live casino' o virtual, i tre gruppi del report
 * (utils/reportCasino.js GRUPPI_SEZIONI). Come per le scommesse, la voce del
 * gettone (SITE_REPORTS_VIEW) e' del livello e vale su tutte le skin.
 */
export function skinConCasino() {
  return sezioneAccesa('casino') || sezioneAccesa('liveCasino') || sezioneAccesa('virtual');
}

/** Le sezioni del catalogo, nell'ordine della riga dei giochi, solo le accese. */
export function sezioniAccese() {
  return catalogo.sezioni.filter((s) => sezioneAccesa(s.chiave));
}

/**
 * I blocchi della Home, nell'ordine in cui vanno disegnati.
 * Ogni voce: {tipo, acceso, impostazioni, colori, def} dove def e' la scheda
 * del tipo nel catalogo. Fuori: i tipi sconosciuti, gli spenti, e quelli di
 * una sezione spenta.
 */
export function blocchiHome() {
  const def = catalogo.moduli.home;
  const scelti = oggetto(moduliDellaSkin().home);
  const lista = scelti && Array.isArray(scelti.blocchi) ? scelti.blocchi : def.diSerie.blocchi;
  return lista
      .filter((b) => b && def.blocchi[b.tipo])
      .filter((b) => b.acceso !== false)
      .filter((b) => !def.blocchi[b.tipo].sezione || sezioneAccesa(def.blocchi[b.tipo].sezione))
      .map((b) => ({
        tipo: b.tipo,
        acceso: true,
        impostazioni: oggetto(b.impostazioni) || {},
        colori: oggetto(b.colori) || null,
        def: def.blocchi[b.tipo],
      }));
}

/** Un'impostazione di un blocco: quella scritta, altrimenti quella di serie. */
export function impostazione(blocco, chiave) {
  const v = blocco.impostazioni[chiave];
  if (v !== undefined && v !== null && v !== '') return v;
  const campo = blocco.def.impostazioni && blocco.def.impostazioni[chiave];
  return campo ? campo.diSerie : undefined;
}

/**
 * Il piede di pagina: contenuto, forma e colori, con il «di serie» del
 * catalogo dove la skin non ha scritto niente.
 *
 * Gli elenchi (riquadri dei punti di forza, voci delle colonne, enti del
 * gioco responsabile) arrivano gia' puliti: una riga senza testo si salta,
 * un indirizzo che non e' una pagina del sito (/…) o un sito (https://…)
 * si toglie. Un elenco che la skin non ha scritto vale quello di serie.
 */
export function pieDiPagina() {
  const def = catalogo.moduli.pieDiPagina;
  const scelto = oggetto(moduliDellaSkin().pieDiPagina) || {};
  const contenuto = oggetto(scelto.contenuto) || {};
  const forma = oggetto(scelto.forma) || {};
  const colori = oggetto(scelto.colori) || {};

  const colonna = (chiave) => {
    const c = oggetto(contenuto[chiave]) || {};
    const acceso = c.acceso === undefined ? def.contenuto[chiave].diSerie : c.acceso !== false;
    return Object.assign({}, c, {acceso});
  };
  const campi = (chiave) => def.contenuto[chiave].campi;
  /* Una colonna di collegamenti: il titolo (o quello di serie) e le voci scritte. */
  const colonnaLink = (chiave) => {
    const c = colonna(chiave);
    return Object.assign(c, {titolo: testoDiSerie(campi(chiave).titolo, c.titolo), voci: vociLink(c.voci)});
  };
  const marchio = colonna('marchio');
  const legale = colonna('legale');
  const giocoResponsabile = colonna('giocoResponsabile');
  const enti = vociLink(giocoResponsabile.enti);
  const fiducia = colonna('fiducia');
  const riquadri = Array.isArray(fiducia.voci)
    ? fiducia.voci.map(oggetto).filter((v) => v && testo(v.titolo))
        .map((v) => ({icona: testo(v.icona).toLowerCase(), titolo: testo(v.titolo), testo: testo(v.testo)}))
    : [];
  return {
    informazioni: colonnaLink('informazioni'),
    giocoResponsabile: Object.assign(giocoResponsabile, {
      enti: Array.isArray(giocoResponsabile.enti) && giocoResponsabile.enti.length ? enti : campi('giocoResponsabile').enti.diSerie,
    }),
    pagamenti: colonna('pagamenti'),
    cripto: Object.assign(colonna('cripto'), {
      monete: sigle(colonna('cripto').monete),
      frase: testoDiSerie(def.contenuto.cripto.campi.frase, colonna('cripto').frase),
    }),
    fiducia: Object.assign(fiducia, {voci: riquadri.length ? riquadri.slice(0, 4) : campi('fiducia').voci.diSerie}),
    marchio: Object.assign(marchio, {
      frase: testo(marchio.frase),
      social: SOCIAL.map((s) => ({chiave: s, indirizzo: indirizzo(marchio[s])})).filter((s) => s.indirizzo && /^https:\/\//.test(s.indirizzo)),
    }),
    colonnaGiochi: colonnaLink('colonnaGiochi'),
    colonnaAiuto: Object.assign(colonnaLink('colonnaAiuto'), {email: email(colonna('colonnaAiuto').email)}),
    colonnaPromozioni: colonnaLink('colonnaPromozioni'),
    risali: colonna('risali'),
    legale: Object.assign(legale, {
      testo: testo(legale.testo),
      societa: testo(legale.societa), sede: testo(legale.sede), registrazione: testo(legale.registrazione),
      ente: testo(legale.ente), licenza: testo(legale.licenza),
      sigillo: testo(legale.sigillo), verifica: indirizzo(legale.verifica),
    }),
    colonne: forma.colonne || def.forma.colonne.diSerie,
    apertura: forma.apertura || def.forma.apertura.diSerie,
    colori: colori.eredita === false ? colori : null,
  };
}

/** I social del piede, nell'ordine in cui si mostrano: le chiavi sono quelle del catalogo. */
export const SOCIAL = ['telegram', 'x', 'instagram', 'facebook', 'tiktok', 'youtube', 'discord'];

function testo(v) {
  return typeof v === 'string' ? v.trim() : '';
}

/** Un indirizzo che si puo' aprire: una pagina del sito (/…) o un sito (http/https). Altrimenti ''. */
function indirizzo(v) {
  const t = testo(v);
  if (/^\/(?!\/)/.test(t)) return t;
  if (/^https?:\/\/[^\s]+$/i.test(t)) return t;
  return '';
}

function email(v) {
  const t = testo(v);
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t) ? t : '';
}

/** Le voci di un elenco «nome + indirizzo», senza le righe vuote o con un indirizzo che non si apre. */
function vociLink(lista) {
  if (!Array.isArray(lista)) return [];
  return lista.map(oggetto).filter(Boolean)
      .map((v) => ({nome: testo(v.nome), indirizzo: indirizzo(v.indirizzo)}))
      .filter((v) => v.nome && v.indirizzo);
}

/** «btc, Eth;usdt» -> ['BTC', 'ETH', 'USDT'], senza doppioni; vuoto -> [] (= tutte). */
function sigle(testo) {
  if (typeof testo !== 'string') return [];
  return Array.from(new Set(testo.split(/[\s,;]+/).map((x) => x.trim().toUpperCase()).filter(Boolean)));
}

/** Un testo scritto dalla skin; vuoto (o non un testo) vale il «di serie» del catalogo. */
function testoDiSerie(campo, scritto) {
  return typeof scritto === 'string' && scritto.trim() ? scritto.trim() : campo.diSerie;
}

/**
 * LA PAGINA DELL'EVENTO (modulo Sport › Pagina evento), per uno sport.
 *
 * `ordine` sono i tipi di mercato nell'ordine deciso dal backoffice per
 * quello sport (vuoto: vale l'ordine delle schede); `inEvidenza` quanti dei
 * primi sono in evidenza; `aperti` quali riquadri arrivano aperti; la
 * disposizione sul computer; se c'e' la ricerca. Statistiche e live sono
 * interruttori spenti nel catalogo: qui escono sempre spenti, col motivo.
 */
export function paginaEvento(sportId) {
  const def = catalogo.moduli.sport.sottomoduli.paginaEvento;
  const sport = oggetto(moduliDellaSkin().sport) || {};
  const pagina = oggetto(sport.paginaEvento) || {};
  const contenuto = oggetto(pagina.contenuto) || {};
  const forma = oggetto(pagina.forma) || {};
  const colori = oggetto(pagina.colori) || {};
  const perSport = oggetto(contenuto.perSport) || {};
  const mio = oggetto(perSport[sportId]) || {};
  const quanti = Number(mio.inEvidenza);
  const scelta = (chiave) => {
    const campo = def.forma[chiave];
    const v = forma[chiave];
    return campo.scelte.some((s) => s.valore === v && !s.spento) ? v : campo.diSerie;
  };
  const interruttore = (chiave) => {
    const campo = def.contenuto[chiave];
    if (campo.spento) return {acceso: false, spento: campo.spento};
    const v = contenuto[chiave];
    return {acceso: v === undefined || v === null ? campo.diSerie : v !== false, spento: ''};
  };
  return {
    ordine: Array.isArray(mio.ordine) ? mio.ordine.map(String) : [],
    inEvidenza: Number.isFinite(quanti) && quanti >= 0 ? quanti : def.contenuto.perSport.inEvidenzaDiSerie,
    aperti: scelta('apertiAllArrivo'),
    disposizione: scelta('disposizione'),
    cerca: interruttore('cerca'),
    statistiche: interruttore('statistiche'),
    live: interruttore('live'),
    colori: colori.eredita === false ? colori : null,
  };
}

/**
 * IL MENU SPORT (modulo Sport › Menu sport): la posizione — sinistra (di
 * serie), destra, o in alto — e cosa c'e' dentro: giorni, cerca, preferiti,
 * numeri. Sul telefono la posizione non conta (il menu e' sempre la
 * striscia), gli interruttori si'.
 */
export function menuSport() {
  const def = catalogo.moduli.sport.sottomoduli.menuSport;
  const sport = oggetto(moduliDellaSkin().sport) || {};
  const menu = oggetto(sport.menuSport) || {};
  const forma = oggetto(menu.forma) || {};
  const contenuto = oggetto(menu.contenuto) || {};
  const scelte = def.forma.posizione.scelte;
  const posizione = scelte.some((s) => s.valore === forma.posizione && !s.spento)
    ? forma.posizione : def.forma.posizione.diSerie;
  const interruttore = (chiave) => {
    const campo = (def.contenuto || {})[chiave];
    if (!campo) return true;
    if (campo.spento) return false;
    /* «spentoSe»: un altro modulo lo spegne (la ricerca in testata spegne
       quella del menu: un posto solo). Vale anche nel backoffice, che
       mostra l'interruttore spento col motivo del catalogo. */
    if (campo.spentoSe && leggiChiave(campo.spentoSe.chiave) === campo.spentoSe.vale) return false;
    const v = contenuto[chiave];
    return v === undefined || v === null ? campo.diSerie !== false : v !== false;
  };
  return {
    posizione,
    contenuto: {
      giorni: interruttore('giorni'),
      cerca: interruttore('cerca'),
      preferiti: interruttore('preferiti'),
      numeri: interruttore('numeri'),
      antepost: interruttore('antepost'),
    },
  };
}

/**
 * I COLORI DELLE DISCIPLINE scelti dalla skin (Sport › Menu sport › colori),
 * per chiave di sport ('calcio', 'tennis'...: la stessa di chiaveSport).
 * Solo i valori che sono colori veri, e solo se la skin ha scelto di non
 * ereditare: altrimenti {} e ogni sport tiene il suo colore di serie
 * (coloriSport.js). Il colore serve a distinguere gli sport, quindi non
 * diventa mai quello della skin.
 */
export function coloriDiscipline() {
  const sport = oggetto(moduliDellaSkin().sport) || {};
  const menu = oggetto(sport.menuSport) || {};
  const colori = oggetto(menu.colori) || {};
  if (colori.eredita !== false) return {};
  const fuori = {};
  Object.keys(catalogo.moduli.sport.sottomoduli.menuSport.colori || {}).forEach((k) => {
    if (eColore(colori[k])) fuori[k] = colori[k].trim();
  });
  return fuori;
}

/**
 * LA QUOTA SCELTA nel colore della skin (Sport › Vista prematch › Quota
 * scelta). Il colore va su tutta la pagina (tabella, schede, telefono,
 * pagina dell'evento, Home) come variabile CSS sulla radice, con accanto il
 * colore del testo che ci si legge sopra; il foglio di stile lo usa solo
 * quando la radice porta data-quota-scelta, cosi' senza scelta l'aspetto
 * resta quello di sempre.
 */
export function coloreQuotaScelta() {
  const v = vistaPrematch().colori;
  return v && eColore(v.scelta) ? v.scelta.trim() : null;
}

/* Testo chiaro o scuro sopra un fondo: la luminanza del colore. */
export function testoSopra(colore) {
  const c = String(colore || '').trim();
  let r; let g; let b;
  const esa = c.match(/^#([0-9a-f]{3,8})$/i);
  if (esa) {
    let h = esa[1];
    if (h.length <= 4) h = h.split('').map((x) => x + x).join('');
    r = parseInt(h.slice(0, 2), 16); g = parseInt(h.slice(2, 4), 16); b = parseInt(h.slice(4, 6), 16);
  } else {
    const n = c.match(/rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i);
    if (!n) return '#ffffff';
    [r, g, b] = [Number(n[1]), Number(n[2]), Number(n[3])];
  }
  const lin = (x) => { const v = x / 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
  const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return L > 0.35 ? '#13191d' : '#ffffff';
}

function applicaQuotaScelta() {
  if (typeof document === 'undefined') return;
  const radice = document.documentElement;
  let colore = null;
  try {
    colore = coloreQuotaScelta();
  } catch (e) {
    colore = null;   // skin non ancora pronta: si riprova alla prossima
  }
  if (colore) {
    radice.style.setProperty('--lvc-quota-scelta', colore);
    radice.style.setProperty('--lvc-quota-scelta-testo', testoSopra(colore));
    radice.setAttribute('data-quota-scelta', '');
  } else {
    radice.style.removeProperty('--lvc-quota-scelta');
    radice.style.removeProperty('--lvc-quota-scelta-testo');
    radice.removeAttribute('data-quota-scelta');
  }
}
/* A ogni skin che arriva (anche la bozza dell'anteprima del backoffice).
   Il controllo serve alle prove che sostituiscono skin.js con un finto. */
if (typeof ascoltaSkin === 'function') ascoltaSkin(applicaQuotaScelta);
applicaQuotaScelta();

/* Un colore vero (#rgb, #rrggbb, rgb(), hsl()): lo stesso controllo di
   skin.js, che vale per i colori dell'aspetto. */
function eColore(v) {
  return typeof v === 'string'
    && /^(#([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})|rgba?\([\d\s.,%/]+\)|hsla?\([\d\s.,%/]+\))$/i.test(v.trim());
}

/**
 * LA VISTA PREMATCH (modulo Sport › Vista prematch): tabella o schede,
 * quante quote in riga nelle schede, per giornata o per ora, i colori.
 * I mercati in riga NON stanno qui: sono le schede dello sport.
 */
export function vistaPrematch() {
  const def = catalogo.moduli.sport.sottomoduli.vistaPrematch;
  const sport = oggetto(moduliDellaSkin().sport) || {};
  const vista = oggetto(sport.vistaPrematch) || {};
  const forma = oggetto(vista.forma) || {};
  const colori = oggetto(vista.colori) || {};
  const scelta = (chiave) => {
    const campo = def.forma[chiave];
    const v = forma[chiave] === undefined || forma[chiave] === null ? undefined : String(forma[chiave]);
    return campo.scelte.some((s) => s.valore === v && !s.spento) ? v : campo.diSerie;
  };
  const quote = scelta('quoteInRiga');
  return {
    disposizione: scelta('disposizione'),
    quoteInRiga: quote === 'tutte' ? Infinity : Number(quote),
    ordine: scelta('ordine'),
    colori: colori.eredita === false ? colori : null,
  };
}

/**
 * LA SCHEDINA (modulo Sport › Schedina): il lato, cosa fa sotto i 1280 px,
 * dove sta la rotellina, i colori. I numeri (importi rapidi, bonus,
 * prenotazione, ultime giocate) NON stanno qui: sono per nodo, in Vincoli ›
 * Impostazioni schedina, e arrivano col profilo di gioco.
 */
export function schedina() {
  const def = catalogo.moduli.sport.sottomoduli.schedina;
  const sport = oggetto(moduliDellaSkin().sport) || {};
  const mia = oggetto(sport.schedina) || {};
  const forma = oggetto(mia.forma) || {};
  const contenuto = oggetto(mia.contenuto) || {};
  const colori = oggetto(mia.colori) || {};
  const scelta = (campo, valore) => {
    const v = valore === undefined || valore === null ? undefined : String(valore);
    return campo.scelte.some((s) => s.valore === v && !s.spento) ? v : campo.diSerie;
  };
  return {
    lato: scelta(def.forma.lato, forma.lato),
    sotto1280: scelta(def.forma.sotto1280, forma.sotto1280),
    rotellina: scelta(def.contenuto.rotellina, contenuto.rotellina),
    colori: colori.eredita === false ? colori : null,
  };
}

/** La posizione del menu sport: sinistra (di serie), destra, o alto. */
export function posizioneMenuSport() {
  return menuSport().posizione;
}

/**
 * LA LOBBY DEL CASINO' (moduli Casino' e Live casino), per la sezione
 * `chiave` ('casino' o 'liveCasino'): cosa c'e' (cerca, fornitori,
 * preferiti, prova), come si dispone (griglia o righe, misura delle
 * copertine) e i colori. Dove la skin non ha scritto niente vale il «di
 * serie» del catalogo; una voce che il catalogo spegne col motivo resta
 * spenta qualunque cosa scriva la skin.
 */
export function lobbyCasino(chiave) {
  const def = catalogo.moduli[chiave] || {};
  const scelto = oggetto(moduliDellaSkin()[chiave]) || {};
  const contenuto = oggetto(scelto.contenuto) || {};
  const forma = oggetto(scelto.forma) || {};
  const colori = oggetto(scelto.colori) || {};
  const interruttore = (k) => {
    const campo = (def.contenuto || {})[k];
    if (!campo || campo.spento) return false;
    const v = contenuto[k];
    return v === undefined || v === null ? campo.diSerie !== false : v !== false;
  };
  const scelta = (k) => {
    const campo = (def.forma || {})[k];
    if (!campo) return undefined;
    const ammesse = (campo.scelte || []).filter((s) => !s.spento).map((s) => s.valore);
    return ammesse.includes(forma[k]) ? forma[k] : campo.diSerie;
  };
  return {
    cerca: interruttore('cerca'),
    fornitori: interruttore('fornitori'),
    preferiti: interruttore('preferiti'),
    prova: interruttore('prova'),
    disposizione: scelta('disposizione') || 'griglia',
    copertine: scelta('copertine') || 'medie',
    inCima: scelta('inCima') || 'fornitori',
    colori: colori.eredita === false ? colori : null,
    schede: schedeDi(def, contenuto),
    attesaGioco: numeroDelCampo((def.contenuto || {}).attesaGioco, contenuto.attesaGioco),
  };
}

/** Un campo «numero» del catalogo: dentro i limiti; niente di scritto vale il «di serie». */
function numeroDelCampo(campo, valore) {
  if (!campo) return 0;
  const minimo = Number.isFinite(Number(campo.minimo)) ? Number(campo.minimo) : 0;
  const massimo = Number.isFinite(Number(campo.massimo)) ? Number(campo.massimo) : Infinity;
  const n = Number(valore);
  if (valore === undefined || valore === null || valore === '' || !Number.isFinite(n)) return Number(campo.diSerie) || 0;
  return Math.min(massimo, Math.max(minimo, n));
}

/**
 * I LOGHI DEI FORNITORI caricati nel backoffice (Moduli › Home › Fornitori),
 * come {nome, immagine, dove}: la Home li mette in fila, la lobby li usa per
 * disegnare il logo al posto del nome nella sua fascia. Si leggono anche col
 * blocco spento o tolto dalla Home: i loghi restano della skin.
 */
export function loghiFornitori() {
  const scelti = oggetto(moduliDellaSkin().home);
  const lista = scelti && Array.isArray(scelti.blocchi) ? scelti.blocchi : catalogo.moduli.home.diSerie.blocchi;
  const blocco = lista.find((b) => b && b.tipo === 'fornitori');
  const loghi = blocco && oggetto(blocco.impostazioni) && Array.isArray(blocco.impostazioni.loghi) ? blocco.impostazioni.loghi : [];
  return loghi
      .map((l) => oggetto(l))
      .filter((l) => l && (String(l.indirizzo || '').trim() || String(l.nome || '').trim()))
      .map((l) => ({nome: String(l.nome || '').trim(), immagine: String(l.indirizzo || '').trim(), dove: String(l.collegamento || '').trim()}));
}

/**
 * LA FASCIA DEI FORNITORI come la regola il backoffice (Moduli › Home ›
 * Fornitori): velocita' (px al secondo) e altezza dei riquadri (px), dentro
 * i limiti del catalogo. Non scritte: undefined, e la fascia usa le sue
 * misure di serie (che nella lobby sono piu' basse che in Home).
 */
export function fasciaFornitori() {
  const def = catalogo.moduli.home.blocchi.fornitori.impostazioni;
  const scelti = oggetto(moduliDellaSkin().home);
  const lista = scelti && Array.isArray(scelti.blocchi) ? scelti.blocchi : [];
  const blocco = lista.find((b) => b && b.tipo === 'fornitori');
  const imp = (blocco && oggetto(blocco.impostazioni)) || {};
  const scritto = (k) => (imp[k] === undefined || imp[k] === null || imp[k] === '' ? undefined : numeroNeiLimiti(def[k], imp[k]));
  return {velocita: scritto('velocita'), altezza: scritto('altezza')};
}

/**
 * LE SCHEDE di una sezione del casino' (Top 10, Jackpot, Roulette...): le
 * compone il backoffice, ognuna con nome, icona e i giochi scelti a mano
 * nell'ordine voluto (moduli.<sezione>.contenuto.schede). Qui solo quelle
 * accese e con un nome, ognuna come {id, nome, icona, quanti, giochi: [id...]}.
 *
 * Una scheda e' un elenco di identificativi e basta: quali di quei giochi si
 * vedono lo decide l'elenco del fornitore (casino-service, con quello che la
 * skin spegne in Gestione casino'). Un id che non c'e' piu' si salta, non si
 * inventa. `quanti` e' quanti giochi mostra la RIGA della scheda (Home,
 * lobby a righe; il catalogo dice da quanto a quanto, al massimo 10): i
 * primi disponibili, nell'ordine della scheda. La PAGINA della scheda
 * (/casino?scheda=top10) li mostra tutti, a pagine.
 *
 * Vuoto se il catalogo non conosce le schede o la skin non ne ha: allora
 * lobby e menu usano le categorie del fornitore, come prima delle schede.
 */
function schedeDi(def, contenuto) {
  const campo = (def.contenuto || {}).schede;
  if (!campo || campo.spento) return [];
  const lista = Array.isArray(contenuto.schede) ? contenuto.schede : (campo.diSerie || []);
  const quanti = misuraDi(campo);
  const viste = new Set();
  return lista
      .map((x) => oggetto(x))
      .filter((x) => x && x.acceso !== false && String(x.nome || '').trim())
      .map((x) => schedaDi(x, quanti))
      .filter((x) => !viste.has(x.id) && viste.add(x.id));
}

/** Quanti giochi mostra la riga di una scheda, dentro i limiti del suo campo nel catalogo (campo.quanti). */
function misuraDi(campo) {
  const limiti = oggetto(campo && campo.quanti) || {};
  const minimo = Number(limiti.minimo) || 1;
  const massimo = Number(limiti.massimo) || 60;
  return (v) => {
    const n = Math.round(Number(v));
    return Number.isFinite(n) && n > 0 ? Math.min(massimo, Math.max(minimo, n)) : (Number(limiti.diSerie) || massimo);
  };
}

/** Una scheda scritta dal backoffice, come la usa il sito: {id, nome, icona, quanti, giochi: [id...]}. */
function schedaDi(x, quanti) {
  return {
    id: String(x.id || x.nome).trim(),
    nome: String(x.nome).trim(),
    icona: String(x.icona || '').trim(),
    quanti: quanti(x.quanti),
    giochi: (Array.isArray(x.giochi) ? x.giochi : [])
        .map((g) => String(oggetto(g) ? g.id : g || '').trim())
        .filter(Boolean),
  };
}

/** Le schede di una sezione del casino' ('casino' o 'liveCasino'): vedi schedeDi. */
export function schedeCasino(chiave) {
  return lobbyCasino(chiave).schede;
}

/**
 * TUTTI i giochi di una scheda presi dall'elenco del fornitore, nell'ordine
 * della scheda: e' la pagina della scheda (o della categoria della Home),
 * che li mostra a pagine. Un id che nell'elenco non c'e' (non piu' mandato,
 * spento per la skin) si salta; uno scritto due volte si conta una.
 */
export function tuttiIGiochiDellaScheda(scheda, elenco) {
  if (!scheda) return [];
  const perId = new Map((elenco || []).map((g) => [g.id, g]));
  const visti = new Set();
  return scheda.giochi
      .filter((id) => perId.has(id) && !visti.has(id) && visti.add(id))
      .map((id) => perId.get(id));
}

/**
 * I giochi della RIGA di una scheda (Home, lobby a righe): i primi `quanti`
 * di tuttiIGiochiDellaScheda. Un id che nell'elenco non c'e' si salta e il
 * posto lo prende quello dopo: la riga resta piena finche' la scheda ne ha.
 */
export function giochiDellaScheda(scheda, elenco) {
  if (!scheda) return [];
  return tuttiIGiochiDellaScheda(scheda, elenco).slice(0, scheda.quanti || undefined);
}

/**
 * LE RIGHE DI GIOCHI DELLA HOME (blocco «righe», impostazione `schede`),
 * nell'ordine della lista. Una voce della lista e' una di due cose:
 *
 *   - 'casino:top10', 'liveCasino:roulette': una scheda di quella sezione,
 *     com'e' scritta li' (nome, quanti, giochi). Una scheda spenta, che non
 *     c'e' piu' o di una sezione spenta non diventa una riga;
 *   - {id, nome, icona, acceso, quanti, giochi: [{id, sezione}...]}: una
 *     categoria SOLO DELLA HOME, con i giochi scelti nel blocco, anche di
 *     Casino' e Live casino insieme. Spenta o senza nome non si vede.
 *
 * Lista vuota (o non scritta): tutte le schede accese, prima Casino' poi
 * Live casino. Ogni riga: {sezione, scheda} con sezione 'casino',
 * 'liveCasino' o 'home'; la scheda di una categoria della Home ha in piu'
 * `servono`, le sezioni accese da cui leggere i suoi giochi.
 *
 * La riga mostra i primi `quanti` giochi (da 5 a 60); il nome della riga e
 * «Vedi tutti» aprono la pagina con tutti: la lobby sulla scheda
 * (/casino?scheda=top10) o, per una categoria della Home, /categoria/<id>.
 */
export function righeDellaHome(scelte, sezioni) {
  const tutte = sezioni.flatMap((k) => schedeCasino(k).map((scheda) => ({sezione: k, scheda})));
  if (!Array.isArray(scelte) || !scelte.length) return tutte;
  const campo = (((catalogo.moduli.home.blocchi.righe || {}).impostazioni) || {}).schede;
  const quanti = misuraDi(campo);
  const viste = new Set();
  return scelte
      .map((v) => {
        const propria = oggetto(v);
        if (!propria) {
          const [k, id] = String(v || '').split(':');
          return tutte.find((r) => r.sezione === k && r.scheda.id === id);
        }
        if (propria.acceso === false || !String(propria.nome || '').trim()) return null;
        /* Le sezioni dei giochi scelti; un gioco scritto senza sezione si cerca in tutte. */
        const dette = (Array.isArray(propria.giochi) ? propria.giochi : []).map((g) => (oggetto(g) ? g.sezione : null));
        const servono = dette.some((k) => !k) ? sezioni : sezioni.filter((k) => dette.includes(k));
        return {sezione: 'home', scheda: Object.assign(schedaDi(propria, quanti), {servono})};
      })
      .filter((r) => r && !viste.has(r.sezione + ':' + r.scheda.id) && viste.add(r.sezione + ':' + r.scheda.id));
}

/** Le sezioni da cui le righe della Home leggono i giochi: Casino' e Live casino, solo le accese. */
export function sezioniDelleRighe() {
  return ['casino', 'liveCasino'].filter((k) => sezioneAccesa(k));
}

/**
 * UNA CATEGORIA SOLO DELLA HOME, per la sua pagina (/categoria/<id>): la
 * riga {sezione: 'home', scheda} com'e' nelle righe della Home, cercata in
 * tutti i blocchi «Righe di giochi» accesi. Null se non c'e': mai scritta,
 * spenta, senza nome, o col blocco spento. Cosi' la pagina mostra solo
 * quello che la Home mostrerebbe.
 */
export function categoriaDellaHome(id) {
  const sezioni = sezioniDelleRighe();
  const chiave = String(id || '').trim();
  return blocchiHome()
      .filter((b) => b.tipo === 'righe')
      .flatMap((b) => righeDellaHome(impostazione(b, 'schede'), sezioni))
      .find((r) => r.sezione === 'home' && r.scheda.id === chiave) || null;
}

/** Un campo di testo di un modulo: quello scritto (senza spazi ai lati), altrimenti quello di serie del catalogo. */
function testoDi(def, scritto, chiave) {
  const v = typeof scritto[chiave] === 'string' ? scritto[chiave].trim() : '';
  if (v) return v;
  const campo = def && def[chiave];
  return campo && typeof campo.diSerie === 'string' ? campo.diSerie : '';
}

/** Un interruttore di un modulo: acceso se la skin non dice niente e il catalogo lo da' di serie. */
function interruttoreDi(def, scritto, chiave) {
  const campo = def && def[chiave];
  if (!campo || campo.spento) return false;
  const v = scritto[chiave];
  return v === undefined || v === null ? campo.diSerie !== false : v !== false;
}

/**
 * LA RICERCA DEI GIOCHI (modulo Ricerca giochi): la casella in testata
 * (stile Nova) e la pagina dei risultati /cerca. Se il catalogo non ha il
 * modulo (un sito piu' vecchio del backoffice non capita, ma una skin con
 * l'albero vuoto si') valgono i suoi «di serie».
 */
export function ricerca() {
  const def = catalogo.moduli.ricerca || {};
  const scelto = oggetto(moduliDellaSkin().ricerca) || {};
  const contenuto = oggetto(scelto.contenuto) || {};
  const forma = oggetto(scelto.forma) || {};
  const colori = oggetto(scelto.colori) || {};
  const campoCopertine = (def.forma || {}).copertine;
  const ammesse = campoCopertine ? campoCopertine.scelte.filter((s) => !s.spento).map((s) => s.valore) : [];
  return {
    casella: interruttoreDi(def.contenuto, contenuto, 'casella'),
    segnaposto: testoDi(def.contenuto, contenuto, 'segnaposto'),
    titolo: testoDi(def.contenuto, contenuto, 'titolo'),
    vuoto: testoDi(def.contenuto, contenuto, 'vuoto'),
    studi: interruttoreDi(def.contenuto, contenuto, 'studi'),
    liveCasino: interruttoreDi(def.contenuto, contenuto, 'liveCasino'),
    copertine: ammesse.includes(forma.copertine) ? forma.copertine : (campoCopertine ? campoCopertine.diSerie : 'medie'),
    colori: colori.eredita === false ? colori : null,
  };
}

/**
 * QUANTI GIOCHI PER PAGINA (modulo Giochi per pagina): uno per tutte le
 * pagine che elencano giochi (lobby, schede, categorie della Home,
 * risultati della ricerca), dentro i limiti del catalogo. Un valore fuori
 * dai limiti vale il limite piu' vicino; niente di scritto vale il «di
 * serie» (50).
 */
export function giochiPerPagina() {
  const campo = ((catalogo.moduli.paginazione || {}).contenuto || {}).perPagina || {};
  const minimo = Number(campo.minimo) || 50;
  const massimo = Number(campo.massimo) || 500;
  const scelto = oggetto(moduliDellaSkin().paginazione) || {};
  const n = Math.round(Number((oggetto(scelto.contenuto) || {}).perPagina));
  if (!Number.isFinite(n) || n <= 0) return Number(campo.diSerie) || 50;
  return Math.min(massimo, Math.max(minimo, n));
}

/**
 * COME SI VA ALLE ALTRE PAGINE (modulo Giochi per pagina › In fondo alla
 * pagina): 'numeri' (‹ 1 2 3 … ›) o 'altre' («Mostra di piu'», che aggiunge
 * una pagina sotto quelle gia' viste). Di serie «Mostra di piu'».
 */
export function modoPagine() {
  const campo = ((catalogo.moduli.paginazione || {}).contenuto || {}).modo;
  const scelto = oggetto(moduliDellaSkin().paginazione) || {};
  const v = (oggetto(scelto.contenuto) || {}).modo;
  const ammesse = campo ? campo.scelte.filter((x) => !x.spento).map((x) => x.valore) : [];
  return ammesse.includes(v) ? v : (campo ? campo.diSerie : 'altre');
}

/**
 * LE PAGINE DI AIUTO (modulo Aiuto: Chi siamo, Regolamento, Assistenza),
 * nell'ordine del catalogo, ognuna come {chiave, percorso, icona, acceso,
 * titolo, testo}. Il testo vuoto di Regolamento e Assistenza vale quello
 * scritto prima nel Piè di pagina › Informazioni: una skin che lo aveva
 * scritto li' non lo perde.
 */
export function pagineAiuto() {
  const def = catalogo.moduli.aiuto || {};
  const scelto = oggetto(moduliDellaSkin().aiuto) || {};
  const informazioni = pieDiPagina().informazioni || {};
  return Object.entries(def.sottomoduli || {}).map(([chiave, sm]) => {
    const mio = oggetto(scelto[chiave]) || {};
    const contenuto = oggetto(mio.contenuto) || {};
    const scritto = testoDi(sm.contenuto, contenuto, 'testo');
    const diPrima = typeof informazioni[chiave] === 'string' ? informazioni[chiave].trim() : '';
    return {
      chiave,
      percorso: sm.percorso,
      icona: sm.icona || '',
      acceso: interruttoreDi(sm.contenuto, contenuto, 'acceso'),
      titolo: testoDi(sm.contenuto, contenuto, 'titolo') || sm.nome,
      testo: scritto || diPrima,
      /* Il modulo «Scrivici» (solo Assistenza): spento di serie, lo riempira' il backend (ticket). */
      scrivici: sm.contenuto.scrivici ? interruttoreDi(sm.contenuto, contenuto, 'scrivici') : false,
    };
  });
}

/** Una pagina di aiuto per chiave ('chiSiamo', 'regolamento', 'assistenza'), o null se il catalogo non la conosce. */
export function paginaAiuto(chiave) {
  return pagineAiuto().find((p) => p.chiave === chiave) || null;
}

/**
 * LA PAGINA CHI SIAMO a sezioni (modulo Aiuto › Chi siamo), come la scrive
 * il backoffice: ogni campo di testo senza spazi ai lati, ogni elenco senza
 * le righe vuote. `ricca` e' vero quando c'e' almeno una parte oltre a
 * titolo e testo: se no la pagina resta il testo semplice di PaginaTesto.
 * Una parte vuota non si disegna: niente testi inventati.
 */
export function chiSiamo() {
  const def = ((catalogo.moduli.aiuto || {}).sottomoduli || {}).chiSiamo || {};
  const campi = def.contenuto || {};
  const mio = oggetto((oggetto(moduliDellaSkin().aiuto) || {}).chiSiamo) || {};
  const scritto = oggetto(mio.contenuto) || {};
  const testo = (k) => testoDi(campi, scritto, k);
  const elenco = (k) => {
    const voci = Object.keys((campi[k] || {}).di || {});
    return (Array.isArray(scritto[k]) ? scritto[k] : [])
        .map((r) => oggetto(r))
        .filter(Boolean)
        .map((r) => Object.fromEntries(voci.map((v) => [v, typeof r[v] === 'string' ? r[v].trim() : ''])))
        .filter((r) => voci.some((v) => r[v]));
  };
  const separati = (v, sep) => String(v || '').split(sep).map((x) => x.trim()).filter(Boolean);
  const pagina = {
    apertura: testo('apertura'),
    presentazione: testo('presentazione'),
    garanzie: separati(testo('garanzie'), ','),
    numeri: elenco('numeri'),
    storiaTitolo: testo('storiaTitolo'),
    storia: testo('testo'),
    citazione: testo('citazione'),
    firma: testo('firma'),
    tappe: elenco('tappe'),
    impegniTitolo: testo('impegniTitolo'),
    impegniTesto: testo('impegniTesto'),
    impegni: elenco('impegni').map((r) => Object.assign(r, {punti: separati(r.punti, ';')})),
    prelieviTitolo: testo('prelieviTitolo'),
    prelieviTesto: testo('prelieviTesto'),
    passi: elenco('passi'),
    monete: interruttoreDi(campi, scritto, 'monete'),
    bonusTitolo: testo('bonusTitolo'),
    bonusTesto: testo('bonusTesto'),
    bonus: elenco('bonus'),
    bonusNota: testo('bonusNota'),
    giochiTitolo: testo('giochiTitolo'),
    giochiTesto: testo('giochiTesto'),
    fornitori: separati(testo('fornitori'), ','),
    fornitoriAltri: testo('fornitoriAltri'),
    responsabileTitolo: testo('responsabileTitolo'),
    responsabileTesto: testo('responsabileTesto'),
    contattiTitolo: testo('contattiTitolo'),
    contatti: elenco('contatti'),
    chiusuraTitolo: testo('chiusuraTitolo'),
    chiusuraTesto: testo('chiusuraTesto'),
    chiusuraPulsante: testo('chiusuraPulsante'),
  };
  pagina.ricca = Object.entries(pagina).some(([k, v]) => k !== 'storia' && k !== 'monete' && (Array.isArray(v) ? v.length > 0 : !!v));
  return pagina;
}

/**
 * LE PAGINE IN PIU' DEL MENU (modulo Pagine: Promozioni, Spazio VIP),
 * nell'ordine del catalogo, ognuna come {chiave, percorso, icona, acceso,
 * titolo}. Spente di serie: una voce del menu compare solo con la sua
 * pagina accesa, perche' un tasto che non porta da nessuna parte e' peggio
 * di nessun tasto. Il titolo di serie e' quello del catalogo, in italiano:
 * chi lo mostra lo traduce finche' la skin non ne scrive uno suo.
 */
export function pagineDelMenu() {
  const def = catalogo.moduli.pagine || {};
  const scelto = oggetto(moduliDellaSkin().pagine) || {};
  return Object.entries(def.sottomoduli || {}).map(([chiave, sm]) => {
    const contenuto = oggetto((oggetto(scelto[chiave]) || {}).contenuto) || {};
    return {
      chiave,
      percorso: sm.percorso,
      icona: sm.icona || '',
      acceso: interruttoreDi(sm.contenuto, contenuto, 'acceso'),
      titolo: testoDi(sm.contenuto, contenuto, 'titolo') || sm.nome,
      titoloDiSerie: (sm.contenuto.titolo || {}).diSerie || sm.nome,
    };
  });
}

/** Una pagina del menu per chiave ('promozioni', 'vip'), o null se il catalogo non la conosce. */
export function paginaDelMenu(chiave) {
  return pagineDelMenu().find((p) => p.chiave === chiave) || null;
}

/* I campi scritti di una pagina del modulo Pagine, coi «di serie» del catalogo. */
function campiDellaPagina(chiave) {
  const def = ((catalogo.moduli.pagine || {}).sottomoduli || {})[chiave] || {};
  const campi = def.contenuto || {};
  const scritto = oggetto((oggetto((oggetto(moduliDellaSkin().pagine) || {})[chiave]) || {}).contenuto) || {};
  const testo = (k) => testoDi(campi, scritto, k);
  const elenco = (k) => {
    const voci = Object.keys((campi[k] || {}).di || {});
    return (Array.isArray(scritto[k]) ? scritto[k] : [])
        .map((r) => oggetto(r))
        .filter(Boolean)
        .map((r) => Object.fromEntries(voci.map((v) => [v, typeof r[v] === 'string' ? r[v].trim() : ((campi[k].di[v] || {}).diSerie || '')])));
  };
  return {testo, elenco};
}

/**
 * LA PAGINA PROMOZIONI (modulo Pagine › Promozioni): l'apertura (c'e' solo
 * con la cifra scritta), le promozioni nell'ordine scritto (senza titolo si
 * saltano), i tipi diversi per le pastiglie, la nota in fondo.
 */
export function promozioni() {
  const {testo, elenco} = campiDellaPagina('promozioni');
  const promo = elenco('promo').filter((r) => r.titolo);
  const tipi = [];
  promo.forEach((r) => { if (r.tipo && !tipi.includes(r.tipo)) tipi.push(r.tipo); });
  const cifra = testo('aperturaCifra');
  return {
    apertura: cifra ? {
      riga: testo('aperturaRiga'), titolo: testo('aperturaTitolo'), cifra,
      pulsante: testo('aperturaPulsante'), collegamento: testo('aperturaCollegamento'), immagine: testo('aperturaImmagine'),
    } : null,
    promo,
    tipi,
    nota: testo('nota'),
  };
}

/**
 * LA PAGINA SPAZIO VIP (modulo Pagine › Spazio VIP): apertura, vantaggi
 * (senza titolo si saltano), i livelli (senza nome si saltano) con le
 * colonne che almeno una riga riempie, il pulsante in fondo.
 */
export function spazioVip() {
  const {testo, elenco} = campiDellaPagina('vip');
  const livelli = elenco('livelli').filter((r) => r.nome);
  const colonne = ['punti', 'bonus', 'compleanno', 'cambio', 'cashback'].filter((k) => livelli.some((r) => r[k]));
  return {
    aperturaTitolo: testo('aperturaTitolo'),
    aperturaRiga: testo('aperturaRiga'),
    aperturaTesto: testo('aperturaTesto'),
    vantaggi: elenco('vantaggi').filter((r) => r.titolo),
    livelliTitolo: testo('livelliTitolo'),
    livelli,
    colonne,
    pulsante: testo('pulsante'),
    collegamento: testo('collegamento'),
  };
}

/**
 * LA PAGINA TORNEI (modulo Pagine › Tornei): i tornei in corso e quelli
 * completati nell'ordine scritto (senza nome si saltano), le etichette di
 * ognuno gia' divise alla virgola, e l'interruttore della classifica.
 *
 * LA CLASSIFICA E LA POSIZIONE DEL GIOCATORE NON CI SONO: li dara' il
 * backend (vedi il documento dell'API). Con l'interruttore acceso la pagina
 * mostra lo scheletro al loro posto, mai numeri inventati.
 */
export function tornei() {
  const {testo, elenco} = campiDellaPagina('tornei');
  const def = (((catalogo.moduli.pagine || {}).sottomoduli || {}).tornei || {}).contenuto || {};
  const scritto = oggetto((oggetto((oggetto(moduliDellaSkin().pagine) || {}).tornei) || {}).contenuto) || {};
  const conEtichette = (r) => Object.assign({}, r, {
    etichette: (r.etichette || '').split(',').map((s) => s.trim()).filter(Boolean),
  });
  return {
    inCorsoTitolo: testo('inCorsoTitolo'),
    inCorso: elenco('inCorso').filter((r) => r.nome).map(conEtichette),
    classifica: interruttoreDi(def, scritto, 'classifica'),
    classificaNota: testo('classificaNota'),
    completatiTitolo: testo('completatiTitolo'),
    completati: elenco('completati').filter((r) => r.nome),
  };
}

/**
 * LE PAGINE SCRITTE DEL MENU (modulo Pagine › Programma fedelta', Ruota
 * della fortuna, Hall of Fame): stessa forma per tutte, apertura col
 * titolo grande, riquadri (senza titolo si saltano), una tabella con le
 * colonne del catalogo (le colonne vuote in tutte le righe non ci sono; le
 * righe senza la prima colonna si saltano), l'interruttore della parte che
 * dara' il backend, il pulsante in fondo.
 */
export function paginaScritta(chiave) {
  const {testo, elenco} = campiDellaPagina(chiave);
  const def = (((catalogo.moduli.pagine || {}).sottomoduli || {})[chiave] || {}).contenuto || {};
  const scritto = oggetto((oggetto((oggetto(moduliDellaSkin().pagine) || {})[chiave]) || {}).contenuto) || {};
  const tutte = Object.entries((def.tabella || {}).di || {}).map(([k, v]) => ({chiave: k, nome: v.nome}));
  const prima = tutte.length ? tutte[0].chiave : 'nome';
  const righe = elenco('tabella').filter((r) => r[prima]);
  return {
    aperturaTitolo: testo('aperturaTitolo'),
    aperturaRiga: testo('aperturaRiga'),
    aperturaTesto: testo('aperturaTesto'),
    riquadri: elenco('riquadri').filter((r) => r.titolo),
    tabellaTitolo: testo('tabellaTitolo'),
    colonne: tutte.filter((c) => righe.some((r) => r[c.chiave])),
    righe,
    backend: interruttoreDi(def, scritto, 'backend'),
    /* Le vincite vere (solo Hall of Fame): il periodo da cui si parte, quante e da quanto in su. */
    periodo: sceltaDi(def.periodo, scritto.periodo, 'mese'),
    quante: Math.round(numeroDelCampo(def.quante, scritto.quante)) || 10,
    minimo: numeroDelCampo(def.minimo, scritto.minimo),
    /* I record di sempre per sezione, sotto le grandi vincite (acceso di serie). */
    record: interruttoreDi(def, scritto, 'record'),
    pulsante: testo('pulsante'),
    collegamento: testo('collegamento'),
  };
}

/**
 * GLI EFFETTI (modulo Effetti): la pioggia di monete e l'avviso al versamento
 * accreditato e al bonus, col suono. Senza il modulo nel catalogo (o con
 * tutto spento) non parte niente. I numeri restano nei limiti del catalogo.
 */
export function effetti() {
  const def = catalogo.moduli.effetti || {};
  const campi = def.contenuto || {};
  const scelto = oggetto(moduliDellaSkin().effetti) || {};
  const contenuto = oggetto(scelto.contenuto) || {};
  return {
    deposito: interruttoreDi(campi, contenuto, 'deposito'),
    bonus: interruttoreDi(campi, contenuto, 'bonus'),
    suono: interruttoreDi(campi, contenuto, 'suono'),
    quante: Math.round(numeroDelCampo(campi.quante, contenuto.quante)),
    durata: numeroDelCampo(campi.durata, contenuto.durata),
    titoloDeposito: testoDi(campi, contenuto, 'titoloDeposito'),
    titoloBonus: testoDi(campi, contenuto, 'titoloBonus'),
  };
}

/**
 * GLI SFONDI DEL SITO (Sito › Skin › Sfondi del sito): per ogni zona del
 * catalogo (alto, pagina, piede) l'immagine del computer e quella del
 * telefono, gia' con l'indirizzo dell'API davanti. '' = nessuna immagine:
 * resta il colore Sfondo. Il telefono senza la sua usa quella del computer.
 * Il backoffice le scrive in moduli.sfondi.<zona>.{computer,telefono}.
 */
export function sfondi() {
  const zone = (catalogo.aspetto && catalogo.aspetto.sfondi && catalogo.aspetto.sfondi.zone) || {};
  const scelto = oggetto(moduliDellaSkin().sfondi) || {};
  const fuori = {};
  Object.keys(zone).forEach((zona) => {
    const z = oggetto(scelto[zona]) || {};
    const computer = immagineSfondo(z.computer);
    fuori[zona] = {computer, telefono: immagineSfondo(z.telefono) || computer};
  });
  return fuori;
}

/* Un'immagine caricata (/api/tenants/file/…) o un indirizzo https; altrimenti ''. */
function immagineSfondo(v) {
  const t = testo(v);
  if (/^\/api\/tenants\/file\/[^\s"'()\\]+$/.test(t)) return indirizzoImmagine(t);
  if (/^https?:\/\/[^\s"'()\\]+$/i.test(t)) return t;
  return '';
}
